import { MenuItemInfo } from '@tableau/widgets';
import React from 'react';
import { connect, Dispatch } from 'react-redux';
import _ from 'underscore';

import { ButtonControlledChildComponentProps } from '~commonInfra/components/ButtonControlledComponent';
import {
  ButtonControlledFlyoutListMenu,
  ButtonControlledFlyoutListMenuOwnProps,
  ButtonControlledFlyoutListMenuProps,
} from '~commonInfra/components/ButtonControlledFlyoutListMenu';
import { MapDispatchToProps, MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { resolveEntityActionClickable, resolveEntityActionVisible } from '~src/resourceActions/helpers/ResourceActionHelpers';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';

import { AppState } from '../../../bootstrap/redux/state/AppState';
import { Messages } from '../../../codegen/Localize';
import { requestAvailableActionsForEntity } from '../../actions/AvailableActionsForEntityActions';
import { IExplorableItemTypeInfo } from '../../models/resourceInfoParts';
import { selectAvailableActionsForEntity } from '../../selectors/EntityActionsSelectors';
import { AvailableActions } from '../../state/AvailableActionsForEntityState';
import { SourceComponentType } from './ActionsMenu';
import { AllActionsAllowed, IEntityAction } from './EntityActions/IEntityAction';
import { anyItemIsUnpublishedWorkbook } from './EntityActions/IEntityActionHelpers';

export type EntityAction = IEntityAction<IExplorableItemTypeInfo, any>;

export interface ActionsMenuPanelStateProps {
  readonly menuItems: ButtonControlledFlyoutListMenuOwnProps['menuItems'];
}

export interface ActionsMenuPanelDispatchProps {
  readonly onComponentDidMount: ButtonControlledFlyoutListMenuOwnProps['onComponentDidMount'];
  readonly onMenuItemSelected: ButtonControlledFlyoutListMenuOwnProps['onMenuItemSelected'];
}

export interface ActionsMenuPanelOwnProps extends Pick<ButtonControlledFlyoutListMenuProps, 'id' | 'role'>{
  readonly entityActionSections: EntityAction[][];
  readonly targetItems: IExplorableItemTypeInfo[];
  readonly availableActions?: AvailableActions|undefined;
  readonly sourceComponent?: SourceComponentType;
}


function buildMenuItems(entityActionSections: EntityAction[][],
                        targetItems: IExplorableItemTypeInfo[],
                        availableActions: AvailableActions | undefined,
  state: AppState,
  sourceComponent: SourceComponentType
): MenuItemInfo[] {
  const visibleActionSections = entityActionSections.map(section => section.filter(a => resolveEntityActionVisible(a, targetItems, state, availableActions, sourceComponent)))
      .filter(section => !_(section).isEmpty());

  const menuItems: MenuItemInfo[] = [];
  for (let i = 0; i < visibleActionSections.length; i++) {
    const section = visibleActionSections[i];
    const sectionItems = section.filter(m => !m.parentName).map(m => {
      return {
        value: m.name,
        displayValue: m.translateName(targetItems, state),
        disabled: !resolveEntityActionClickable(m, availableActions, targetItems, state),
        ...(m.isChecked
          ? { checked: m.isChecked(availableActions, targetItems, state) }
          : {}),
        menuItemInfos: (() => {
          const subMenuItems: MenuItemInfo[] = [];
          section.filter(subm => subm.parentName === m.name).forEach(subm =>
            subMenuItems.push({
              value: subm.name,
              displayValue: subm.translateName(targetItems, state),
              disabled: !resolveEntityActionClickable(subm, availableActions, targetItems, state),
              ...(subm.isChecked
                ? { checked: subm.isChecked(availableActions, targetItems, state) }
                : {}),
            })
          );
          return subMenuItems.length > 0 ? subMenuItems: undefined;
        })()
      };
    });
    sectionItems.forEach(item => menuItems.push(item));

    if (i < visibleActionSections.length - 1) {
      menuItems.push({ isDivider: true });
    }
  }

  // TFSID 1126917: need to display something in the menu if there are no actions, or else the menu panel appears as a small blank square
  if (menuItems.length === 0) {
    menuItems.push({
      disabled: true,
      displayValue: Messages.none(),
      value: Messages.none(),
    });
  }
  return menuItems;
}

export const mapStateToProps: MapStateToProps<ActionsMenuPanelStateProps, ActionsMenuPanelOwnProps> =
  (state: AppState, ownProps: ActionsMenuPanelOwnProps) => {

    let availableActions;
    if (ownProps.targetItems.length !== 1 || anyItemIsUnpublishedWorkbook([ownProps.targetItems[0] as IContentInfo])) {
      availableActions = AllActionsAllowed; // Replace this with the actual permissions in story 752767
    } else if (ownProps.targetItems.length === 1) {
      availableActions = selectAvailableActionsForEntity(state, ownProps.targetItems[0]);
    }

    return ({
      menuItems: buildMenuItems(ownProps.entityActionSections, ownProps.targetItems, availableActions, state, ownProps.sourceComponent),
    });
  };

export const mapDispatchToProps: MapDispatchToProps<ActionsMenuPanelDispatchProps, ActionsMenuPanelOwnProps> =
  (dispatch: Dispatch<AppState>, ownProps: ActionsMenuPanelOwnProps) => ({
    onComponentDidMount: () => {
      if (ownProps.targetItems.length === 1) {
        if (anyItemIsUnpublishedWorkbook(ownProps.targetItems as IContentInfo[])) {
          return;
        }

        // TODO: Make this dispatch less noisy (it shouldn't have to call the API every time the user toggles the menu)
        const item = ownProps.targetItems[0];
        dispatch(requestAvailableActionsForEntity(item));
      }
    },
    onMenuItemSelected: (menuItemValue: string) => {
      const entityActions: EntityAction[] = _.flatten(ownProps.entityActionSections);
      const entityAction: EntityAction = _.find(entityActions, a => menuItemValue === a.name);
      const reduxAction = entityAction.createReduxAction(ownProps.targetItems);
      dispatch(reduxAction);
    },
  });

/**
 * This component takes an item and a global list of actions, and combines them with information from
 * the Redux store to produce menu items and handlers that can be passed into the FlyoutListMenuWidget.
 */
export const ConnectedActionsMenuPanel: React.ComponentType<ActionsMenuPanelOwnProps & ButtonControlledChildComponentProps> =
    connect(mapStateToProps, mapDispatchToProps)(ButtonControlledFlyoutListMenu);
